import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createAccount, getAccount, updateAccount } from '@/api/accounts';
import { listPeople } from '@/api/people';
import { ApiError } from '@/api/client';
import { accountSchema, accountSubtypes, accountSources, type AccountFormData } from './schema';
import type { SystemAccountWrite } from '@/types/oscar';

const emptyDefaults: AccountFormData = {
  DisplayName: '',
  Subtype: 'ServiceAccount',
  Source: 'Manual',
  UPN: '',
  EntraObjectId: '',
  OwnerPersonId: null,
  Purpose: '',
  Notes: '',
  Active: true,
};

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm';

export function AccountEditForm() {
  const { id } = useParams();
  const accountId = id ? Number(id) : null;
  const isEdit = accountId !== null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCloud, setIsCloud] = useState(false);

  const accountQuery = useQuery({
    queryKey: ['account', accountId],
    queryFn: ({ signal }) => getAccount(accountId!, signal),
    enabled: isEdit,
  });

  const form = useForm<AccountFormData>({ resolver: zodResolver(accountSchema), defaultValues: emptyDefaults });
  const { register, handleSubmit, reset, setValue, watch, formState } = form;

  useEffect(() => {
    if (accountQuery.data) {
      const a = accountQuery.data;
      const cloud = a.Source === 'EntraCloud';
      setIsCloud(cloud);
      reset({
        DisplayName: a.DisplayName,
        Subtype: a.Subtype,
        // EntraCloud (and any non-manual source) isn't selectable in this form;
        // show Manual in the (disabled) select. Source is never sent for cloud rows.
        Source: (accountSources as readonly string[]).includes(a.Source)
          ? (a.Source as AccountFormData['Source'])
          : 'Manual',
        UPN: a.UPN ?? '',
        EntraObjectId: a.EntraObjectId ?? '',
        OwnerPersonId: a.OwnerPersonId,
        Purpose: a.Purpose ?? '',
        Notes: a.Notes ?? '',
        Active: a.Active,
      });
    }
  }, [accountQuery.data, reset]);

  function toPayload(data: AccountFormData): SystemAccountWrite {
    const blank = (v?: string) => (v && v.trim() !== '' ? v.trim() : undefined);
    const payload: SystemAccountWrite = {
      DisplayName: data.DisplayName.trim(),
      Subtype: data.Subtype,
      UPN: blank(data.UPN),
      EntraObjectId: blank(data.EntraObjectId),
      OwnerPersonId: data.OwnerPersonId ?? undefined,
      Purpose: blank(data.Purpose),
      Notes: blank(data.Notes),
      Active: data.Active,
    };
    // Never overwrite a sync-owned EntraCloud source from this manual form.
    if (!isCloud) payload.Source = data.Source;
    return payload;
  }

  const mutation = useMutation({
    mutationFn: (data: AccountFormData) =>
      isEdit ? updateAccount(accountId!, toPayload(data)) : createAccount(toPayload(data)),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['account', saved.SystemAccountId] });
      navigate(`/accounts/${saved.SystemAccountId}`);
    },
    onError: (err) => setSubmitError(err instanceof ApiError ? err.message : 'Could not save the account.'),
  });

  const ownerId = watch('OwnerPersonId');

  return (
    <section className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-navy">{isEdit ? 'Edit account' : 'Add account'}</h1>

      {submitError && (
        <div role="alert" className="mt-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{submitError}</div>
      )}

      <form onSubmit={handleSubmit((d) => { setSubmitError(null); mutation.mutate(d); })} className="mt-5 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">
            Display name
            <input {...register('DisplayName')} className={inputCls} />
            {formState.errors.DisplayName && <span className="mt-1 block font-normal text-red">{formState.errors.DisplayName.message}</span>}
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            Subtype
            <select {...register('Subtype')} className={inputCls}>
              {accountSubtypes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            Source {isCloud && <span className="font-normal text-darkGrey/60">(managed by sync)</span>}
            <select {...register('Source')} disabled={isCloud} className={inputCls}>
              {accountSources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            UPN
            <input {...register('UPN')} className={inputCls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            Entra object ID
            <input {...register('EntraObjectId')} placeholder="GUID" className={inputCls} />
            {formState.errors.EntraObjectId && <span className="mt-1 block font-normal text-red">{formState.errors.EntraObjectId.message}</span>}
          </label>
          <label className="block text-xs font-semibold text-darkGrey">
            Owner
            <OwnerPicker value={ownerId ?? null} onChange={(v) => setValue('OwnerPersonId', v, { shouldDirty: true })} />
          </label>
          <label className="flex items-center gap-2 pt-5 text-xs font-semibold text-darkGrey">
            <input type="checkbox" {...register('Active')} /> Active
          </label>
        </div>

        <label className="block text-xs font-semibold text-darkGrey">
          Purpose
          <textarea {...register('Purpose')} rows={2} className={inputCls} />
        </label>
        <label className="block text-xs font-semibold text-darkGrey">
          Notes
          <textarea {...register('Notes')} rows={2} className={inputCls} />
        </label>

        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className="rounded bg-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create account'}
          </button>
          <button type="button" onClick={() => navigate(isEdit ? `/accounts/${accountId}` : '/accounts')} className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-darkGrey hover:bg-bg">
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

/** Search People to set the accountable owner. */
function OwnerPicker({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const results = useQuery({
    queryKey: ['people-search', term],
    queryFn: ({ signal }) => listPeople({ search: term, pageSize: 8 }, signal),
    enabled: open && term.trim().length >= 2,
  });

  if (value) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <span className="rounded bg-bg px-2 py-1 text-sm font-normal text-navy">Owner #{value}</span>
        <button type="button" onClick={() => onChange(null)} className="text-xs font-normal text-red hover:underline">Clear</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input value={term} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} placeholder="Search to set an owner" aria-label="Owner search" className={inputCls} />
      {open && results.data && results.data.Items.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded border border-border bg-white text-sm shadow">
          {results.data.Items.map((p) => (
            <li key={p.PersonId}>
              <button type="button" onClick={() => { onChange(p.PersonId); setOpen(false); setTerm(''); }} className="block w-full px-3 py-1.5 text-left font-normal hover:bg-bg">
                {p.DisplayName} <span className="text-darkGrey/60">({p.PrimaryEmail})</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
